import React, { useEffect, useLayoutEffect, useRef } from 'react'
import type {
  ClearIndicatorProps,
  ControlProps,
  DropdownIndicatorProps,
  IndicatorsContainerProps,
  MenuListProps,
  OptionProps,
  GroupBase,
} from 'react-select'
import type { SelectOption } from './Select.types'

// ---------------------------------------------------------------------------
// TypeScript augmentation — позволяет передавать floatingLabel в <Select>
// ---------------------------------------------------------------------------

declare module 'react-select/dist/declarations/src/Select' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> {
    floatingLabel?: string
    floatingRequired?: boolean
    // Props for the search input rendered inside the dropdown menu.
    // Passed via <Select menuSearchQuery=... /> so MenuListWithSearch
    // always gets fresh values through selectProps without being remounted.
    menuSearchQuery?: string
    onMenuSearchChange?: (q: string) => void
    onMenuSearchMouseDown?: (e: React.MouseEvent<HTMLInputElement>) => void
    onMenuSearchKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
    menuHasMore?: boolean
    onMenuLoadMore?: () => void
    sizerText?: string
  }
}

// ---------------------------------------------------------------------------
// cx utility
// ---------------------------------------------------------------------------

type CxArg = string | Record<string, boolean | undefined | null> | undefined | null | false

export function cx(...args: CxArg[]): string {
  const out: string[] = []
  for (const arg of args) {
    if (!arg) continue
    if (typeof arg === 'string') out.push(arg)
    else for (const [k, v] of Object.entries(arg)) if (v) out.push(k)
  }
  return out.join(' ')
}

// ---------------------------------------------------------------------------
// Label
// ---------------------------------------------------------------------------

export function Label({ text, required }: { text: string; required?: boolean }) {
  return (
    <label className={cx('rd-dropdown__label', { 'rd-dropdown__label--required': !!required })}>
      {text}
    </label>
  )
}

// ---------------------------------------------------------------------------
// Custom react-select components
// ---------------------------------------------------------------------------

// Стрелка — позиционируется абсолютно через _dropdown.scss
// rd-icon-arrow-down задаёт mask-image для иконки
export function DropdownIndicator<O, M extends boolean, G extends GroupBase<O>>(
  _props: DropdownIndicatorProps<O, M, G>,
) {
  return (
    <span
      className="rd-dropdown__arrow rd-icon-arrow-down"
      style={{ position: 'static', alignSelf: 'center', marginTop: 0 }}
      aria-hidden
    />
  )
}

// Кнопка очистки — innerProps содержит onClick от react-select
export function ClearIndicator<O, M extends boolean, G extends GroupBase<O>>(
  { innerProps }: ClearIndicatorProps<O, M, G>,
) {
  return (
    <button {...(innerProps as React.HTMLAttributes<HTMLButtonElement>)} type="button" className="rd-dropdown__clear is-visible">
      <span className="rd-icon-cross" />
    </button>
  )
}

// Скрываем анимированные точки загрузки — состояние загрузки отражается иначе
export function LoadingIndicator() {
  return null
}

// Обёртка индикаторов — абсолютное позиционирование, как в _dropdown.scss
export function IndicatorsContainer<O, M extends boolean, G extends GroupBase<O>>(
  { children, innerProps }: IndicatorsContainerProps<O, M, G>,
) {
  return (
    <div
      {...innerProps}
      style={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        paddingRight: '12px',
        gap: '4px',
      }}
    >
      {children}
    </div>
  )
}

// Control с поддержкой floating label внутри wrapper'а.
// Floating label обязан быть ВНУТРИ rd-dropdown__wrapper — иначе CSS-позиционирование не работает.
export function RdControl<O, M extends boolean, G extends GroupBase<O>>({
  children,
  innerRef,
  innerProps,
  selectProps,
}: ControlProps<O, M, G>) {
  return (
    <div ref={innerRef} {...innerProps} className="rd-dropdown__wrapper">
      {children}
      {selectProps.floatingLabel && (
        <Label text={selectProps.floatingLabel} required={selectProps.floatingRequired} />
      )}
      {selectProps.sizerText && (
        <span className="rd-dropdown__sizer" aria-hidden="true">{selectProps.sizerText}</span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// MenuList with search input inside + pagination (for lookup and chips select)
// ---------------------------------------------------------------------------

type MenuListBase = MenuListProps<unknown, boolean, GroupBase<unknown>>

/**
 * MenuList with an embedded search input and infinite-scroll pagination.
 *
 * Dynamic state (query, hasMore, callbacks) is read from selectProps so that
 * this component can be passed as a STABLE reference to react-select's
 * `components` prop. A stable reference means react-select never remounts the
 * menu on each keystroke, so the search input keeps focus while typing.
 */
export function MenuListWithSearch({
  children,
  innerRef,
  innerProps,
  className,
  selectProps,
}: MenuListBase) {
  const {
    menuSearchQuery: searchQuery = '',
    onMenuSearchChange: onSearchChange,
    onMenuSearchMouseDown: onSearchMouseDown,
    onMenuSearchKeyDown: onSearchKeyDown,
    menuHasMore: hasMore = false,
    onMenuLoadMore: onLoadMore,
  } = selectProps

  // Восстанавливаем фокус после каждого рендера — react-select может перехватывать
  // фокус на свой dummy input при обновлении опций/состояния загрузки.
  const inputRef = useRef<HTMLInputElement>(null)
  const hadFocusRef = useRef(false)
  useLayoutEffect(() => {
    if (hadFocusRef.current && inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.focus()
    }
  })

  // Автофокус поиска — чтобы печатать сразу после открытия меню.
  useEffect(() => {
    inputRef.current?.focus()
    hadFocusRef.current = true
  }, [])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (hasMore && scrollHeight - scrollTop - clientHeight < 60) {
      onLoadMore?.()
    }
  }

  return (
    <div>
      <div className="rd-dropdown__search">
        <div className="rd-dropdown__search-wrapper">
          <input
            ref={inputRef}
            type="text"
            className="rd-dropdown__search-input"
            placeholder="Поиск"
            autoComplete="off"
            value={searchQuery}
            onChange={e => onSearchChange?.(e.target.value)}
            onFocus={() => { hadFocusRef.current = true }}
            onBlur={() => { hadFocusRef.current = false }}
            onMouseDown={onSearchMouseDown}
            onKeyDown={e => {
              // Блокируем всплытие ДО react-select — иначе он перехватывает
              // Backspace (удаляет тег), Enter (сабмит формы) и другие клавиши.
              e.stopPropagation()
              onSearchKeyDown?.(e)
            }}
          />
          {searchQuery && (
            <button
              type="button"
              className="rd-dropdown__search-clear is-visible"
              onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onSearchChange?.('') }}
            >
              <span className="rd-icon-cross" />
            </button>
          )}
          <span className="rd-dropdown__search-icon rd-icon-search" />
        </div>
      </div>
      <div
        ref={innerRef}
        {...innerProps}
        className={className}
        style={{ maxHeight: 210, overflowY: 'auto', position: 'relative' }}
        onScroll={handleScroll}
      >
        {children}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Option with checkbox (for chips multi-select)
// ---------------------------------------------------------------------------

export function ChipsOption<O, G extends GroupBase<O>>({
  isSelected,
  innerRef,
  innerProps,
  children,
}: OptionProps<O, true, G>) {
  return (
    <div ref={innerRef} {...innerProps} className={cx('rd-dropdown__item', { 'rd-dropdown__item--selected': isSelected })}>
      <span className="rd-checkbox rd-checkbox--no-label">
        <input type="checkbox" className="rd-checkbox__input" checked={isSelected} readOnly tabIndex={-1} />
        <span className="rd-checkbox__box">
          <span className="rd-icon-done" />
        </span>
      </span>
      <span className="rd-dropdown__item-text">{children}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared lookup fetch — compatible with ajax_select and new-format endpoints
// ---------------------------------------------------------------------------

/**
 * Universal fetch for lookup endpoints.
 *
 * Sends both `term` and `q` so the function works with:
 *   - ajax_select channels (/ajax_select/ajax_lookup/<channel>)
 *       — use `term`, 0-based `page`, return array [{pk, repr, ...}]
 *   - new-format endpoints
 *       — use `q`, 1-based `page`, return {results: [{pk, label}], has_more: bool}
 *
 * Auto-detects response format by checking if it is an array.
 */
export async function fetchLookupPage(
  url: string,
  query: string,
  page: number,
  pageSize: number,
  idField?: string,
): Promise<{ options: SelectOption[]; hasMore: boolean }> {
  const params = new URLSearchParams({
    term: query,          // ajax_select
    q: query,             // new-format endpoints
    page: String(page - 1), // ajax_select uses 0-based
    page_size: String(pageSize),
  })
  const data: unknown = await fetch(`${url}?${params}`).then(r => r.json())

  // ajax_select format: [{pk, value, match, repr}]
  // value = get_result() (e.g. guid), repr = format_item_display() (human-readable)
  if (Array.isArray(data)) {
    const options = (data as Array<Record<string, unknown>>).map(r => ({
      value: String(idField ? (r[idField] ?? r.pk) : r.pk),
      label: String(r.value ?? r.repr ?? r.pk),
    }))
    return { options, hasMore: options.length >= pageSize }
  }

  // Object format — {results: [{pk, label} | {id, text}], has_more | pagination.more}
  const obj = data as Record<string, unknown>
  const raw = (obj.results as Array<Record<string, unknown>>) ?? []
  const options = raw.map(r => ({
    value: String(idField ? (r[idField] ?? r.pk ?? r.id) : (r.pk ?? r.id ?? '')),
    label: String(r.label ?? r.text ?? r.pk ?? r.id ?? ''),
  }))
  const hasMore =
    (obj.has_more as boolean | undefined) ??
    ((obj.pagination as Record<string, unknown> | undefined)?.more as boolean | undefined) ??
    options.length >= pageSize

  return { options, hasMore }
}

// ---------------------------------------------------------------------------
// Shared styles prop — только структурные inline-стили, не семантика
// ---------------------------------------------------------------------------

export const selectLayoutStyles = {
  valueContainer: (_: object, { isMulti }: { isMulti: boolean }) => ({
    flex: 1,
    // Multi: position:relative so placeholder overlays input inside valueContainer.
    // Single: position:static so placeholder positions relative to rd-dropdown__wrapper.
    position: isMulti ? 'relative' as const : 'static' as const,
    overflow: 'hidden' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    flexWrap: (isMulti ? 'wrap' : 'nowrap') as 'wrap' | 'nowrap',
    // Multi: CSS wrapper (_dropdown.scss) already has padding: 6px 36px 6px 8px
    // Single: match $rd-input-padding-x (15px); indicators are position:absolute
    ...(isMulti ? { gap: '4px' as const } : { padding: '0 34px 0 15px' }),
  }),
  control: () => ({}),
  // override CSS overflow:hidden on .rd-dropdown__menu so child can scroll;
  // handle top/bottom placement — CSS mixin sets top:calc(100%+4px),
  // so we override via inline styles based on react-select's computed placement.
  menu: (_: object, { placement }: { placement: string }) => ({
    overflow: 'visible' as const,
    ...(placement === 'top'
      ? { top: 'auto', bottom: 'calc(100% + 12px)' }
      : { top: 'calc(100% + 4px)', bottom: 'auto' }),
  }),
  // explicit scroll container — react-select removes these with unstyled=true
  menuList: () => ({ maxHeight: 240, overflowY: 'auto' as const, position: 'relative' as const }),
  option: () => ({}),
  singleValue: () => ({}),
  // Placeholder is out of flex flow so it doesn't compete with the input container (flex:1).
  // CSS class rd-dropdown__placeholder provides padding: 0 $rd-input-padding-x (15px).
  placeholder: () => ({
    position: 'absolute' as const,
    top: '50%',
    left: 0,
    right: 0,
    transform: 'translateY(-50%)',
    pointerEvents: 'none' as const,
    overflow: 'hidden' as const,
    whiteSpace: 'nowrap' as const,
    textOverflow: 'ellipsis' as const,
  }),
  input: () => ({ flex: 1 }),
  multiValue: () => ({}),
  multiValueLabel: () => ({}),
  multiValueRemove: () => ({}),
  clearIndicator: () => ({}),
  dropdownIndicator: () => ({}),
  noOptionsMessage: () => ({}),
  loadingMessage: () => ({}),
}

// ---------------------------------------------------------------------------
// classNames builder
// ---------------------------------------------------------------------------

interface ClassNamesOpts {
  size?: 'medium' | 'large'
  floating?: boolean
  error?: string
  isMulti?: boolean
  isOpen: boolean
  isFocused?: boolean
}

export function buildClassNames({ size = 'medium', floating, error, isMulti, isOpen, isFocused }: ClassNamesOpts) {
  return {
    container: ({ isDisabled, hasValue }: { isDisabled: boolean; hasValue: boolean }) =>
      cx(
        'rd-dropdown',
        `rd-dropdown--${size}`,
        isMulti && 'rd-dropdown--multi',
        {
          'rd-dropdown--floating': !!floating,
          'rd-dropdown--disabled': isDisabled,
          'rd-dropdown--opened': isOpen,
          'rd-dropdown--focus': !!isFocused && !isOpen,
          'rd-dropdown--error': !!error,
          'has-value': hasValue,
        },
      ),
    control: () => 'rd-dropdown__wrapper',
    valueContainer: () => '',
    singleValue: () => 'rd-dropdown__value-text rd-dropdown__value-text--filled',
    placeholder: () => 'rd-dropdown__placeholder',
    input: () => 'rd-dropdown__chips-input',
    indicatorsContainer: () => '',
    menu: () => 'rd-dropdown__menu',
    menuList: () => 'rd-dropdown__items',
    option: ({ isSelected }: { isSelected: boolean }) =>
      cx('rd-dropdown__item', { 'rd-dropdown__item--selected': isSelected }),
    multiValue: () => 'rd-dropdown__chip',
    multiValueLabel: () => 'rd-dropdown__chip-text',
    multiValueRemove: () => 'rd-dropdown__chip-remove',
    clearIndicator: () => '',
    dropdownIndicator: () => '',
    noOptionsMessage: () => 'rd-dropdown__empty',
    loadingMessage: () => 'rd-dropdown__loading',
  }
}
